import { ReactNode } from "react";

import { SxProps, Theme, useTheme } from "@mui/material";
import { AvailablePaymentCurrency, formatNarrowCurrencyAmount, getDollarValue } from "@simplyk/common";

import { FlipCard } from "./FlipCard";

import { useLocaleContext } from "@/contexts/LocaleContext";
import { FormThermometer } from "@/forms-design-system/FormThermometer/FormThermometer";
import { HeartsFormThermometer } from "@/forms-design-system/HeartsFormThermometer/HeartsFormThermometer";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";

export type FormCampaignTargetThermometerProps = {
  id?: string;
  currentCollectedAmount: number;
  targetedAmount: number;
  currency: AvailablePaymentCurrency;
  label: {
    renderCurrentAmount: (currentCollectedAmount: string, targetedAmount: string) => ReactNode;
    ongoingCampaign0To49: ReactNode;
    ongoingCampaign50To79: ReactNode;
    ongoingCampaign80To99: ReactNode;
    campaignAchieved: ReactNode;
  };
  sx?: SxProps<Theme>;
};

export const FormCampaignTargetThermometer = ({
  id,
  currentCollectedAmount,
  targetedAmount,
  currency,
  label: labelProp,
  sx,
}: FormCampaignTargetThermometerProps) => {
  const theme = useTheme();
  const { isoLocale } = useLocaleContext();

  if (!theme.palette.forms || !theme.palette.formsComponents?.thermometer) {
    return <InvalidThemeErrorMessage />;
  }

  const formattedCurrentCollectedAmount = formatNarrowCurrencyAmount(getDollarValue(currentCollectedAmount), {
    locale: isoLocale,
    currency,
    withCents: false,
  });

  const formattedTargetedAmount = formatNarrowCurrencyAmount(getDollarValue(targetedAmount), {
    locale: isoLocale,
    currency,
    withCents: false,
  });

  const percentage = (currentCollectedAmount / targetedAmount) * 100;
  const label = labelProp.renderCurrentAmount(formattedCurrentCollectedAmount, formattedTargetedAmount);
  const isCampaignAchieved = currentCollectedAmount >= targetedAmount;

  const front = isCampaignAchieved ? (
    <HeartsFormThermometer label={label} percentage={percentage} animated loop />
  ) : (
    <FormThermometer label={label} percentage={percentage} />
  );

  const getBackLabel = () => {
    if (isCampaignAchieved) {
      return labelProp.campaignAchieved;
    }

    if (percentage >= 80) {
      return labelProp.ongoingCampaign80To99;
    }

    if (percentage >= 50) {
      return labelProp.ongoingCampaign50To79;
    }

    return labelProp.ongoingCampaign0To49;
  };

  const back = <FormThermometer label={getBackLabel()} percentage={percentage} />;

  return <FlipCard id={id} front={front} back={back} height="62px" sx={sx} />;
};
